import { useAuth } from '../context/AuthContext'
import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import LogoUpload from '../components/LogoUpload'
import { Capacitor } from '@capacitor/core'

export default function Settings() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [timezone, setTimezone] = useState(profile?.clinics?.timezone || 'America/New_York')
  const [savingTz, setSavingTz] = useState(false)
  const [tzSaved, setTzSaved] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  ]

  async function handleDeleteAccount() {
    if (deleteConfirm !== 'DELETE') return
    setDeleting(true)
    setDeleteError('')
    try {
      const { error } = await supabase.rpc('delete_my_account')
      if (error) { setDeleteError(error.message); setDeleting(false); return }
      await supabase.auth.signOut()
      navigate('/login')
    } catch (err) {
      setDeleteError('Something went wrong. Please try again.')
      setDeleting(false)
    }
  }

  async function handleSaveTimezone() {
    setSavingTz(true)
    await supabase.from('clinics').update({ timezone }).eq('id', profile.clinic_id)
    setSavingTz(false)
    setTzSaved(true)
    setTimeout(() => setTzSaved(false), 2000)
  }

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician')} className="back-link" style={{ cursor: 'pointer' }}>← Dashboard</span>
        <div className="page-header">
          <h1>Settings</h1>
          <p>{profile?.clinics?.name}</p>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 15, marginBottom: '1rem' }}>Clinic branding</h2>
          <div style={{ marginBottom: '1rem' }}>
            {profile?.clinics?.logo_url ? (
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Current logo</p>
                <img
                  src={profile.clinics.logo_url}
                  alt="Clinic logo"
                  style={{ height: 48, width: 'auto', objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', padding: 8, background: 'var(--bg)' }}
                />
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>No logo uploaded yet. Your clinic name will appear as text.</p>
            )}
            <LogoUpload onUploaded={() => window.location.reload()} />
          </div>
        </div>

        {profile?.is_admin && !Capacitor.isNativePlatform() && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: 15, marginBottom: '1rem' }}>Billing</h2>
            <p style={{ fontSize: 14, marginBottom: '1rem' }}>Manage your subscription, seats, and invoices.</p>
            <button className="secondary" onClick={() => navigate('/billing')} style={{ fontSize: 14 }}>
              Go to billing →
            </button>
          </div>
        )}

        {profile?.is_admin && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: 15, marginBottom: '0.5rem' }}>Clinic timezone</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem' }}>Used to determine the current day for patient compliance tracking.</p>
            <select value={timezone} onChange={e => setTimezone(e.target.value)} style={{ marginBottom: 12 }}>
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <button onClick={handleSaveTimezone} disabled={savingTz} style={{ padding: '10px 20px', fontSize: 14 }}>
              {savingTz ? 'Saving...' : tzSaved ? 'Saved!' : 'Save timezone'}
            </button>
          </div>
        )}

        <div className="card">
          <h2 style={{ fontSize: 15, marginBottom: '1rem' }}>Invite codes</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 2px' }}>Patients → completept.app/signup</p>
              <p style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', letterSpacing: 2, margin: 0 }}>{profile?.clinics?.invite_code}</p>
            </div>
            <div style={{ padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 2px' }}>Clinicians → completept.app/signup/clinician</p>
              <p style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', letterSpacing: 2, margin: 0 }}>{profile?.clinics?.clinician_invite_code}</p>
            </div>
          </div>
        </div>
      {profile?.is_admin && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: 15, marginBottom: '0.5rem' }}>Support</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem' }}>Need help? Reach out and we'll get back to you as soon as possible.</p>
          <a href="mailto:support@completept.app" style={{ fontSize: 14, color: 'var(--brand)', textDecoration: 'none', fontWeight: 500 }}>support@completept.com</a>
        </div>
      )}

      <div className="card" style={{ marginTop: '1.5rem', borderColor: '#fecaca' }}>
        <h2 style={{ fontSize: 15, marginBottom: '0.5rem', color: '#dc2626' }}>Delete account</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        <button className="danger" onClick={() => setShowDelete(true)} style={{ fontSize: 13 }}>
          Delete my account
        </button>
      </div>

      {showDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowDelete(false) }}>
          <div style={{ background: 'var(--surface)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, padding: '1.5rem' }}>
            <h2 style={{ fontSize: 17, marginBottom: 8, color: '#dc2626' }}>Delete account</h2>
            <p style={{ fontSize: 14, marginBottom: '1.5rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              This will permanently delete your account and all your data. Type <strong>DELETE</strong> to confirm.
            </p>
            {deleteError && <div className="alert-error" style={{ marginBottom: '1rem' }}>{deleteError}</div>}
            <div className="form-group">
              <input
                placeholder="Type DELETE to confirm"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm !== 'DELETE' || deleting}
                  style={{ flex: 1, padding: 14 }}
                >
                  {deleting ? 'Deleting...' : 'Delete my account'}
                </button>
                <button className="secondary" onClick={() => { setShowDelete(false); setDeleteConfirm('') }} style={{ flex: 1, padding: 14 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}